import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import type { Nullable } from "primereact/ts-helpers";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchReferences } from "../../../../api/application/reference";
import { fetchStatisticsByReference } from "../../../../api/application/statistics";
import { fetchTables } from "../../../../api/application/table";
import type { RequestError } from "../../../../api/infrastructure/statistics.api";
import type { Statistics } from "../../../../api/infrastructure/statistics.formatter";
import type { APITable } from "../../../../api/infrastructure/table.formatter";
import { getGeneratedReferences } from "../../../../api/shared/get-references";
import { apiRequestWrapper } from "../../../../api/shared/http-client";
import { appLog } from "../../../../context";
import { useAppStore } from "../../../../store/app.store";
import { createDayJs, dayJsFormat } from "../../../shared/global-tools/dayjs.helpers";
import Navbar from "../../parts/Navbar/Navbar";
import OurDropdown from "../../utils/OurDropdown/OurDropdown";
import Spinner from "../../utils/Spinner/Spinner";
import StatisticsPart from "./StatisticsPart/StatisticsPart";
import "./StatisticsScreen.scss";

export default function StatisticsScreen() {
  const wrapAsync = useWrapAsync();
  const [referenceId, setReferenceId] = useState<number>();
  const [tableId, setTableId] = useState<number>();
  const [selectedReferenceId, setSelectedReferenceId] = useState<number>();
  const [selectedTableId, setSelectedTableId] = useState<number>();
  const { generatedReferences, setGeneratedReferences } = useAppStore();
  const [tables, setTables] = useState<APITable[]>();
  const [statistics, setStatistics] = useState<Statistics | RequestError>();
  const [startDate, setStartDate] = useState<Nullable<Date>>(null);
  const [endDate, setEndDate] = useState<Nullable<Date>>(null);
  const { pathname } = useLocation();

  const handleStartDateChange = (newStartDate: Nullable<Date>) => {
    if (!newStartDate) {
      setStartDate(null);
      return;
    }
    if (endDate && newStartDate > endDate) {
      return;
    }
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (newEndDate: Nullable<Date>) => {
    if (!newEndDate) {
      setEndDate(null);
      return;
    }
    if (startDate && newEndDate < startDate) {
      return;
    }
    setEndDate(newEndDate);
  };

  const handleStatisticsByReference = wrapAsync(async () => {
    if (selectedTableId === undefined) return;
    setReferenceId(selectedReferenceId);
    setTableId(selectedTableId);
    setStatistics(undefined);
    const options =
      startDate || endDate
        ? {
            startedAt: startDate
              ? dayJsFormat(startDate, "YYYY-MM-DD")
              : dayJsFormat(new Date(), "YYYY-MM-DD"),
            endedAt: endDate
              ? dayJsFormat(endDate, "YYYY-MM-DD")
              : dayJsFormat(createDayJs(new Date()).add(1, "year").toISOString(), "YYYY-MM-DD"),
          }
        : undefined;
    try {
      setStatistics(await fetchStatisticsByReference(selectedTableId, options));
    } catch (error) {
      const message = error instanceof Error ? error.message : "une erreur est survenue";
      appLog.error(error);
      setStatistics({
        error: message,
      });
    }
  });

  useEffect(
    wrapAsync(async () => {
      if (generatedReferences) return;
      await apiRequestWrapper(async () => {
        const result = await fetchReferences({ limit: 200, page: 1 });
        setGeneratedReferences(getGeneratedReferences(result));
      });
    }),
    [generatedReferences, pathname],
  );
  useEffect(
    wrapAsync(async () => {
      if (!selectedReferenceId) return;
      await apiRequestWrapper(async () => {
        const result = await fetchTables(selectedReferenceId);
        setTables(result);
      });
    }),
    [selectedReferenceId],
  );

  const referenceOptions = useMemo(
    () =>
      generatedReferences?.map((d) => ({
        label: d.name,
        value: d.id,
      })) ?? [],
    [generatedReferences],
  );

  const tableOptions = useMemo(
    () =>
      tables?.map((d) => ({
        label: d.name,
        value: d.id,
      })) ?? [],
    [tables],
  );

  if (!generatedReferences) return <Spinner />;
  return (
    <div>
      <Navbar pageTitle="Statistiques" />

      <div className="StatisticsScreen Container">
        <div className="Container-content">
          <form
            className="StatisticsScreen-statsFilterForm"
            onSubmit={(e) => {
              e.preventDefault();
              handleStatisticsByReference();
            }}
          >
            <label className="Field w-full">
              <span className="Field-label">Sélectionner un référentiel</span>
              <OurDropdown<number>
                selectedOption={selectedReferenceId}
                initialOptions={referenceOptions}
                onChange={(value) => {
                  setSelectedReferenceId(value);
                }}
                filter
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label">Sélectionner une table</span>
              <OurDropdown<number>
                selectedOption={selectedTableId}
                initialOptions={tableOptions}
                onChange={(value) => {
                  setSelectedTableId(value);
                }}
                filter
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label">Début</span>
              <Calendar
                value={startDate}
                onChange={(e) => handleStartDateChange(e.value)}
                readOnlyInput
                className="w-full"
                showIcon
                placeholder="Date de début"
                maxDate={endDate || undefined}
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label">Fin</span>
              <Calendar
                value={endDate}
                onChange={(e) => handleEndDateChange(e.value)}
                readOnlyInput
                className="w-full"
                showIcon
                placeholder="Date de fin"
                minDate={startDate || undefined}
              />
            </label>

            <div>
              <Button
                type="submit"
                className="Btn lg primary"
                label="Valider"
                disabled={selectedTableId === undefined}
              />
            </div>
          </form>
          <hr className="StatisticsScreen-divider" />

          {referenceId && tableId && statistics && (
            <div style={{ marginTop: "85px" }}>
              <StatisticsPart
                defaultReferenceId={referenceId}
                defaultTableId={tableId}
                statistics={statistics}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
